import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MapPin,
  Calendar,
  Clock,
  Camera,
  FileText,
  Building2,
  Users,
  Timer,
  PencilRuler,
  Download,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  PageHeader,
  LoadingSpinner,
  ErrorState,
  StatusBadge,
} from "@/components/shared";
import {
  useSite,
  useSiteSummary,
  useActivities,
  useTimeEntries,
  useSiteAssignments,
  useSiteInvoices,
  useCreateSiteInvoice,
  downloadSiteInvoicePdf,
} from "@/lib/api/hooks";
import { useAuthStore } from "@/lib/auth/authStore";
import { toast } from "sonner";
import type { SiteInvoice, TimeEntry, WorkType } from "@/types/sites";
import { TimeEntryDialog } from "./TimeEntryDialog";
import { ActivityFeed } from "./ActivityFeed";
import { StatusChangeModal } from "./StatusChangeModal";
import { CreateNoteModal } from "./CreateNoteModal";
import { CameraUploadFlow } from "./CameraUploadFlow";
import { MediaViewer } from "./MediaViewer";
import { ProjectAssignmentsSection } from "./ProjectAssignmentsSection";
import { SitePlanningCalendar } from "./SitePlanningCalendar";
import { ProjectPlanningSheet } from "./ProjectPlanningSheet";
import { CreateInvoiceDialog } from "./CreateInvoiceDialog";
import {
  buildMediaViewerPath,
  buildSiteDetailPath,
  buildSiteDetailsPath,
  buildSiteTimePath,
  resolveMediaViewerTarget,
} from "./mediaViewerRoute";

function formatDate(date: string | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatCurrency(cents: number | null): string {
  if (cents == null) return "-";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}

function formatInvoiceDate(date: string | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getInvoiceStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: "Entwurf",
    generated: "Erstellt",
    void: "Storniert",
  };

  return labels[status] ?? status;
}

function getWorkTypeLabel(workType: WorkType): string {
  const labels: Record<WorkType, string> = {
    site: "Baustelle",
    workshop: "Werkstatt",
    travel: "Fahrt",
    other: "Sonstiges",
  };

  return labels[workType];
}

const WRAP_VALUE_CLASS = "break-words [overflow-wrap:anywhere]";

type SiteSubview = "overview" | "details" | "time";

function resolveSubview(pathname: string, siteId: string): SiteSubview {
  if (pathname === buildSiteDetailsPath(siteId)) {
    return "details";
  }

  if (pathname === buildSiteTimePath(siteId)) {
    return "time";
  }

  return "overview";
}

export default function SiteDetailPage() {
  const { id, activityId, attachmentId } = useParams<{
    id: string;
    activityId?: string;
    attachmentId?: string;
    slug?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCameraFlow, setShowCameraFlow] = useState(false);
  const [showPlanningSheet, setShowPlanningSheet] = useState(false);
  const [showCreateInvoiceDialog, setShowCreateInvoiceDialog] = useState(false);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [selectedTimeEntry, setSelectedTimeEntry] = useState<TimeEntry | null>(
    null,
  );
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    string | null
  >(null);

  const { data: site, isLoading, error, refetch } = useSite(id!);
  const { data: siteSummary } = useSiteSummary(id!);
  const { data: activities, refetch: refetchActivities } = useActivities(id!);
  const { data: timeEntries } = useTimeEntries(id!);
  const { data: assignments } = useSiteAssignments(id!);
  const isAdmin = user?.role === "admin";
  const {
    data: invoices,
    isLoading: invoicesLoading,
    error: invoicesError,
  } = useSiteInvoices(id!, isAdmin);
  const createInvoice = useCreateSiteInvoice();

  const viewerTarget = useMemo(
    () => resolveMediaViewerTarget(activities || [], activityId, attachmentId),
    [activities, activityId, attachmentId],
  );
  const invoiceRows = invoices ?? [];
  const activeSubview = resolveSubview(location.pathname, id || "");

  if (isLoading) {
    return <LoadingSpinner className="min-h-[400px]" size="lg" />;
  }

  if (error || !site) {
    return (
      <ErrorState
        message="Baustelle konnte nicht geladen werden"
        onRetry={() => refetch()}
      />
    );
  }

  const currentSite = site;
  const viewerPath = viewerTarget
    ? buildMediaViewerPath(
        site.id,
        viewerTarget.activity.id,
        viewerTarget.attachment.attachment_id,
        viewerTarget.title,
      )
    : buildSiteDetailPath(id || "");

  const totalHours = siteSummary?.labor.total_hours ?? 0;
  const materialSummary = siteSummary?.materials;
  const visibleInvoiceRows = showAllInvoices
    ? invoiceRows
    : invoiceRows.slice(0, 1);
  const hiddenInvoiceCount = Math.max(
    invoiceRows.length - visibleInvoiceRows.length,
    0,
  );

  async function handleCreateInvoice(
    invoiceRequest: import("@/types/sites").CreateProjectInvoiceRequest,
  ) {
    try {
      await createInvoice.mutateAsync({
        siteId: currentSite.id,
        ...invoiceRequest,
      });
      toast.success("Rechnung erstellt");
    } catch {
      toast.error("Rechnung konnte nicht erstellt werden");
    }
  }

  async function handleDownloadInvoice(invoice: SiteInvoice) {
    setDownloadingInvoiceId(invoice.id);
    try {
      const blob = await downloadSiteInvoicePdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice.invoice_number_display || "rechnung"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch {
      toast.error("PDF konnte nicht geladen werden");
    } finally {
      setDownloadingInvoiceId(null);
    }
  }

  function navigateToSubview(view: SiteSubview) {
    if (view === "details") {
      navigate(buildSiteDetailsPath(currentSite.id));
      return;
    }

    if (view === "time") {
      navigate(buildSiteTimePath(currentSite.id));
      return;
    }

    navigate(buildSiteDetailPath(currentSite.id));
  }

  function renderTimeEntriesCard(limit?: number) {
    const visibleTimeEntries = limit
      ? (timeEntries || []).slice(0, limit)
      : timeEntries || [];
    const hasHiddenEntries = Boolean(
      limit && timeEntries && timeEntries.length > limit,
    );

    return (
      <Card className="min-w-0">
        <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold">
              Zeiterfassung
            </CardTitle>
            {activeSubview === "overview" ? (
              <p className="text-sm text-muted-foreground">
                Die letzten Buchungen auf einen Blick.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Alle gebuchten Zeiten mit Bearbeitungsmöglichkeit für eigene
                Einträge.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs font-normal">
              {totalHours.toFixed(1)}h gesamt
            </Badge>
            {activeSubview === "overview" && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs"
                onClick={() => navigateToSubview("time")}
              >
                Alle anzeigen
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!timeEntries || timeEntries.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="mb-3 rounded-2xl bg-accent/60 p-3">
                <Clock className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                Noch keine Zeiteintrage
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {visibleTimeEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-accent/40"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium capitalize">
                      {getWorkTypeLabel(entry.work_type)}
                    </p>
                    <div className="space-y-0.5">
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.work_date).toLocaleDateString("de-DE")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Erfasst von {entry.creator_name}
                      </p>
                      {entry.notes && (
                        <p className="truncate text-xs text-muted-foreground">
                          {entry.notes}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-normal">
                      {entry.hours}h
                    </Badge>
                    {entry.can_edit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => {
                          setSelectedTimeEntry(entry);
                          setShowTimeDialog(true);
                        }}
                      >
                        Bearbeiten
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {hasHiddenEntries && timeEntries && (
                <p className="pt-2 text-center text-xs text-muted-foreground">
                  +{timeEntries.length - limit!} weitere Eintrage
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  function renderPlanningCard() {
    return (
      <Card className="min-w-0 md:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">
            Projektplanung
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-accent/25 p-3">
              <p className="text-xs text-muted-foreground">Zeitraum</p>
              <p className="mt-1 text-sm font-medium">
                {currentSite.start_date || currentSite.end_date
                  ? `${formatDate(currentSite.start_date)} – ${formatDate(currentSite.end_date)}`
                  : "Noch kein Zeitraum geplant"}
              </p>
            </div>
            <div className="rounded-lg bg-accent/25 p-3">
              <p className="text-xs text-muted-foreground">Projektart</p>
              <p className="mt-1 text-sm font-medium">
                {currentSite.project_type === "internal_workshop"
                  ? "Werkstattprojekt"
                  : "Baustelle"}
              </p>
            </div>
          </div>

          <ProjectAssignmentsSection
            siteId={currentSite.id}
            assignments={assignments || []}
          />

          <div className="space-y-2">
            <p className="text-sm font-medium">Terminplan</p>
            <SitePlanningCalendar
              siteId={currentSite.id}
              assignments={assignments || []}
              canEdit={isAdmin}
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderOverviewSection() {
    return (
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">
              Projekt auf einen Blick
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid min-w-0 gap-4 sm:grid-cols-2">
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent flex-shrink-0">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {currentSite.project_type === "internal_workshop"
                      ? "Bezug"
                      : "Kunde"}
                  </p>
                  <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                    {currentSite.customer_name || "-"}
                  </p>
                </div>
              </div>
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent flex-shrink-0">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Geplante Tage</p>
                  <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                    {currentSite.estimated_days || "-"}
                  </p>
                </div>
              </div>
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent flex-shrink-0">
                  <Timer className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    Gebuchte Stunden
                  </p>
                  <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                    {totalHours.toFixed(1)}h
                  </p>
                </div>
              </div>
              <div className="flex min-w-0 items-start gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent flex-shrink-0">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Zugewiesen</p>
                  <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                    {assignments?.length || 0} Mitarbeiter
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm font-medium">Mehr Projektkontext</p>
                <p className="text-sm text-muted-foreground">
                  Beschreibung, Abrechnung und Materialübersicht im
                  Detailbereich.
                </p>
              </div>
              <Button
                variant="outline"
                className="h-9"
                onClick={() => navigateToSubview("details")}
              >
                Zu den Details
              </Button>
            </div>
          </CardContent>
        </Card>

        {renderTimeEntriesCard(5)}

        {renderPlanningCard()}
      </div>
    );
  }

  function renderDetailsSection() {
    return (
      <Card className="min-w-0">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">
            Projektdetails
          </CardTitle>
        </CardHeader>
        <CardContent className="min-w-0 space-y-4">
          {currentSite.description && (
            <div className="min-w-0">
              <p className="mb-1 text-xs font-medium uppercase tracking-widest text-muted-foreground">
                Beschreibung
              </p>
              <p className={`${WRAP_VALUE_CLASS} text-sm leading-relaxed`}>
                {currentSite.description}
              </p>
            </div>
          )}

          {(currentSite.budget_amount_cents != null ||
            currentSite.invoice_pricing_mode ||
            currentSite.hourly_rate_cents != null ||
            currentSite.fixed_price_cents != null ||
            currentSite.quote_reference ||
            currentSite.billing_reference ||
            currentSite.billing_notes) && (
            <>
              <Separator />
              <div className="min-w-0">
                <p className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  Budget & Abrechnung
                </p>
                <div className="min-w-0 space-y-3 rounded-lg bg-accent/25 p-4">
                  <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Budget</p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {formatCurrency(currentSite.budget_amount_cents)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Gebuchte Stunden
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {siteSummary
                          ? `${siteSummary.labor.total_hours.toFixed(1)}h`
                          : "-"}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Angebotsreferenz
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {currentSite.quote_reference || "-"}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Materialentnahmen
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {siteSummary
                          ? siteSummary.materials.withdrawal_count
                          : 0}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Abrechnungsreferenz
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {currentSite.billing_reference || "-"}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Rechnungslogik
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {currentSite.invoice_pricing_mode === "fixed_price"
                          ? "Pauschalpreis"
                          : currentSite.invoice_pricing_mode === "hourly_rate"
                            ? "Stundensatz"
                            : "-"}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Verbrauchte Materialien
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {siteSummary
                          ? siteSummary.materials.distinct_material_count
                          : 0}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Stundensatz
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {formatCurrency(currentSite.hourly_rate_cents)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">
                        Pauschalpreis
                      </p>
                      <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                        {formatCurrency(currentSite.fixed_price_cents)}
                      </p>
                    </div>
                  </div>
                  {currentSite.billing_notes && (
                    <div className="min-w-0">
                      <p className="mb-1 text-xs text-muted-foreground">
                        Abrechnungshinweise
                      </p>
                      <p
                        className={`${WRAP_VALUE_CLASS} text-sm leading-relaxed`}
                      >
                        {currentSite.billing_notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {isAdmin && (
            <>
              <Separator />
              <div className="min-w-0">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
                      Rechnungen
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      PDFs aus den gebuchten Projektzeiten und
                      Materialentnahmen.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowCreateInvoiceDialog(true)}
                    disabled={createInvoice.isPending}
                    className="h-10 w-full gap-2 sm:w-auto"
                  >
                    <FileText className="h-4 w-4" />
                    {createInvoice.isPending
                      ? "Wird erstellt..."
                      : "Rechnung erstellen"}
                  </Button>
                </div>

                <div className="space-y-2">
                  {invoicesLoading && (
                    <div className="rounded-lg bg-accent/25 p-3 text-sm text-muted-foreground">
                      Rechnungen werden geladen...
                    </div>
                  )}

                  {invoicesError && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                      Rechnungen konnten nicht geladen werden.
                    </div>
                  )}

                  {!invoicesLoading &&
                    !invoicesError &&
                    invoiceRows.length === 0 && (
                      <div className="rounded-lg bg-accent/25 p-3 text-sm text-muted-foreground">
                        Noch keine Rechnungen für dieses Projekt.
                      </div>
                    )}

                  {visibleInvoiceRows.map((invoice) => {
                    const date = invoice.issued_at ?? invoice.created_at;
                    const hasPdf = invoice.pdf_artifact != null;
                    const isDownloading = downloadingInvoiceId === invoice.id;

                    return (
                      <div
                        key={invoice.id}
                        className="flex flex-col gap-3 rounded-lg bg-accent/25 p-3 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                            <p
                              className={`${WRAP_VALUE_CLASS} min-w-0 max-w-full basis-full break-all text-sm font-medium sm:basis-auto`}
                            >
                              {invoice.invoice_number_display || invoice.id}
                            </p>
                            <Badge
                              variant="outline"
                              className="text-xs font-normal"
                            >
                              {getInvoiceStatusLabel(invoice.status)}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatInvoiceDate(date)}
                            {hasPdf
                              ? " · PDF verfügbar"
                              : " · PDF noch nicht verfügbar"}
                          </p>
                        </div>
                        {hasPdf ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadInvoice(invoice)}
                            disabled={isDownloading}
                            className="h-9 w-full gap-2 sm:w-auto"
                          >
                            <Download className="h-4 w-4" />
                            {isDownloading ? "Lädt..." : "PDF"}
                          </Button>
                        ) : (
                          <Badge
                            variant="outline"
                            className="w-fit text-xs font-normal"
                          >
                            PDF folgt
                          </Badge>
                        )}
                      </div>
                    );
                  })}

                  {invoiceRows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 self-start px-2 text-xs"
                      onClick={() => setShowAllInvoices((current) => !current)}
                    >
                      {showAllInvoices
                        ? "Weniger Rechnungen anzeigen"
                        : `Ältere Rechnungen anzeigen (${hiddenInvoiceCount})`}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          <Separator />

          {siteSummary && (
            <>
              <div className="min-w-0">
                <p className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  Projektkennzahlen
                </p>
                <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Materialien</p>
                    <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                      {materialSummary?.distinct_material_count || 0}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Entnahmen</p>
                    <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                      {materialSummary?.withdrawal_count || 0}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      Baustelle-Stunden
                    </p>
                    <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                      {siteSummary.labor.site_hours.toFixed(1)}h
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      Werkstatt-Stunden
                    </p>
                    <p className={`${WRAP_VALUE_CLASS} text-sm font-medium`}>
                      {siteSummary.labor.workshop_hours.toFixed(1)}h
                    </p>
                  </div>
                </div>
              </div>

              {materialSummary && materialSummary.lines.length > 0 && (
                <>
                  <Separator />
                  <div className="min-w-0">
                    <p className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground">
                      Materialverbrauch
                    </p>
                    <div className="space-y-3">
                      {materialSummary.lines.slice(0, 4).map((line) => (
                        <div
                          key={line.material_id}
                          className="flex flex-col gap-3 rounded-lg bg-accent/25 p-3 sm:flex-row sm:items-start sm:justify-between"
                        >
                          <div className="min-w-0">
                            <p
                              className={`${WRAP_VALUE_CLASS} text-sm font-medium`}
                            >
                              {line.material_name}
                            </p>
                            <p
                              className={`${WRAP_VALUE_CLASS} text-xs text-muted-foreground`}
                            >
                              {line.category_name}
                            </p>
                          </div>
                          <div className="shrink-0 sm:text-right">
                            <p className="text-sm font-medium">
                              {line.total_withdrawn} {line.unit}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {line.withdrawal_count} Entnahmen
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={site.name}
        description={site.customer_name}
        backTo="/sites"
        action={
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setShowPlanningSheet(true)}
                className="gap-2 h-10"
              >
                <PencilRuler className="h-4 w-4" />
                Planen
              </Button>
            )}
            <Button
              onClick={() => setShowTimeDialog(true)}
              className="gap-2 h-10 shadow-sm"
            >
              <Clock className="h-4 w-4" />
              Zeit buchen
            </Button>
          </div>
        }
      />

      <Card className="overflow-hidden">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              {isAdmin ? (
                <div
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowStatusModal(true)}
                >
                  <StatusBadge status={site.status} />
                </div>
              ) : (
                <StatusBadge status={site.status} />
              )}
              <Badge variant="outline" className="text-xs font-normal">
                {site.project_type === "internal_workshop"
                  ? "Werkstattprojekt"
                  : "Baustelle"}
              </Badge>
              {site.location && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  <span>{site.location}</span>
                </div>
              )}
            </div>
            {(site.start_date || site.end_date) && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span>
                  {formatDate(site.start_date)} – {formatDate(site.end_date)}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeSubview} className="space-y-4">
        <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-muted/60 p-1 sm:w-auto">
          <TabsTrigger
            className="min-w-[120px]"
            value="overview"
            onClick={() => navigateToSubview("overview")}
          >
            Übersicht
          </TabsTrigger>
          <TabsTrigger
            className="min-w-[120px]"
            value="details"
            onClick={() => navigateToSubview("details")}
          >
            Details
          </TabsTrigger>
          <TabsTrigger
            className="min-w-[120px]"
            value="time"
            onClick={() => navigateToSubview("time")}
          >
            Zeiten
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeSubview === "overview" && (
        <>
          <Card>
            <CardHeader className="space-y-3 pb-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1.5">
                  <CardTitle className="text-base font-semibold">
                    Projekt-Timeline
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Der zentrale Ort für Notizen, Fotos und Dokumente rund um
                    dieses Projekt.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 gap-2"
                    onClick={() => setShowCameraFlow(true)}
                  >
                    <Camera className="h-4 w-4" />
                    <span>Kamera</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 gap-2"
                    onClick={() => setShowNoteModal(true)}
                  >
                    <FileText className="h-4 w-4" />
                    <span>Eintrag</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ActivityFeed activities={activities || []} siteId={site.id} />
            </CardContent>
          </Card>

          {renderOverviewSection()}
        </>
      )}

      {activeSubview === "details" && renderDetailsSection()}
      {activeSubview === "time" && renderTimeEntriesCard()}

      <TimeEntryDialog
        open={showTimeDialog}
        onOpenChange={(open) => {
          setShowTimeDialog(open);
          if (!open) {
            setSelectedTimeEntry(null);
          }
        }}
        siteId={site.id}
        siteName={site.name}
        mode={selectedTimeEntry ? "edit" : "create"}
        {...(selectedTimeEntry ? { initialData: selectedTimeEntry } : {})}
      />

      <StatusChangeModal
        open={showStatusModal}
        onOpenChange={setShowStatusModal}
        siteId={site.id}
        siteName={site.name}
        currentStatus={site.status}
        onSuccess={() => refetch()}
      />

      <CreateNoteModal
        open={showNoteModal}
        onOpenChange={setShowNoteModal}
        siteId={site.id}
        onSuccess={() => refetchActivities()}
      />

      <CameraUploadFlow
        open={showCameraFlow}
        onOpenChange={setShowCameraFlow}
        siteId={site.id}
        onSuccess={() => refetchActivities()}
      />

      <ProjectPlanningSheet
        open={showPlanningSheet}
        onOpenChange={setShowPlanningSheet}
        site={site}
      />
      <CreateInvoiceDialog
        open={showCreateInvoiceDialog}
        onOpenChange={setShowCreateInvoiceDialog}
        site={site}
        totalHours={totalHours}
        materialLines={materialSummary?.lines ?? []}
        isPending={createInvoice.isPending}
        onSubmit={handleCreateInvoice}
      />

      <MediaViewer
        open={Boolean(activityId && attachmentId)}
        sharePath={viewerPath}
        target={viewerTarget}
        onClose={() => navigate(buildSiteDetailPath(site.id))}
      />
    </div>
  );
}
